import React, { useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { addToRecentlyViewed } from 'lib/recentlyViewed'
import { isMobileOnly } from 'react-device-detect'

import B2BColoredBanner from 'components/B2BColoredBanner/B2BColoredBanner'
import FeatureFlag from 'components/FeatureFlag'
import MarkdownContent from 'components/MarkdownContent/MarkdownContent'
import NoSSR from 'components/NoSSR'
import RecentlyViewedProfiles from 'components/RecentlyViewedProfiles/RecentlyViewedProfiles'
import TagsBreadcrumbs from 'components/TagsBreadcrumbs/TagsBreadcrumbs'
import Wrapper from 'components/Wrapper/Wrapper'
import useEffectOnce from 'hooks/useEffectOnce'
import useElementRelativePosition from 'hooks/useElementRelativePosition'
import useIsProfileDefaultLanguage from 'hooks/useIsProfileDefaultLanguage'
import useMarket from 'hooks/useMarket'
import useRouter from 'hooks/useRouter'
import useScrollPosition from 'hooks/useScrollPosition'

import AboutMemmo from '../AboutMemmo/AboutMemmo'
import ActionBar from './ActionBar/ActionBar'
import BookCTAs from './BookCTAs/BookCTAs'
import Categories from './Categories/Categories'
import Description from './Description/Description'
import Information from './Information/Information'
import Products from './Products/Products'
import ProductSelect from './ProductSelect/ProductSelect'
import ProfileCollections from './ProfileCollections/ProfileCollections'
import RecommendedProfileCollection from './RecommendedProfileCollection/RecommendedProfileCollection'
import TrustSection from './TrustSection/TrustSection'
import { EdgeProps, NodeProps, ProfilePageProps } from './type'
import Videos from './Videos/Videos'

import styles from './ProfilePage.module.css'

const B2B_CHILD_PRODUCT = 'BUSINESS_USE'

const ProfilePage: React.FC<ProfilePageProps> = ({
  profile,
  profileSlug,
  loading,
  color = 'light',
}) => {
  const router = useRouter()

  useEffect(() => {
    if (profile && Object.keys(profile).length) {
      addToRecentlyViewed(profileSlug)
    }
    window.scrollTo(0, 0)
    window.sessionStorage.setItem(
      'hasWrittenInstruction',
      JSON.stringify(false),
    )
  }, [profile, profileSlug])

  const {
    profileProducts,
    featureFlags,
    acceptingOrders,
    defaultProfileProduct,
    defaultMarketId,
    mainTag,
  } = profile || {}
  const filteredProfileProducts =
    loading && defaultProfileProduct
      ? Array(defaultProfileProduct)
      : profileProducts?.edges
          .map((edge: EdgeProps) => {
            const { node } = edge

            if (
              node.product.productType === 'DONATION' &&
              !featureFlags.donation
            )
              return null
            return node.acceptingOrders && node.showOnProfilePage && node
          })
          .filter((node: NodeProps) => node)

  const b2cProfileProducts = filteredProfileProducts?.filter(
    (node: NodeProps) => node?.product?.saleMode === 'B2C',
  )

  const b2bProfileProducts = filteredProfileProducts?.filter(
    (node: NodeProps) =>
      node?.product?.saleMode === 'B2B' ||
      node?.productChildren?.some(
        ({ product }) => product?.productType === B2B_CHILD_PRODUCT,
      ),
  )

  const bookableProducts = {
    b2c: b2cProfileProducts,
    b2b: b2bProfileProducts,
    haveB2C: () => !!b2cProfileProducts?.length,
    haveB2B: () => !!b2bProfileProducts?.length,
    haveOnlyB2B: () =>
      !!b2bProfileProducts?.length && !b2cProfileProducts?.length,
  }

  const [showProductModal, setShowProductModal] = useState(false)
  const [isB2b, setIsB2b] = useState(false)

  const bookButtonsRef = useRef()
  const [showActionBar, setShowActionBar] = useState(false)
  const market = useMarket()
  const isProfileDefaultLanguage = useIsProfileDefaultLanguage(defaultMarketId)

  const isDefaultMarket = defaultMarketId
    ? market.id === defaultMarketId
    : false
  const [showDescription, setShowDescription] = useState(
    isProfileDefaultLanguage,
  )

  const onScroll = () => {
    setShowDescription(true)
    window.removeEventListener('scroll', onScroll)
  }

  useEffectOnce(() => window.addEventListener('scroll', onScroll))

  const ColumnWrapper = ({ children, ...props }: any) =>
    isMobileOnly ? children : <div {...props}>{children}</div>

  const profileOffline =
    !acceptingOrders || (acceptingOrders && !filteredProfileProducts?.length)

  useScrollPosition({
    useEffect: () => {
      const elementRelativePosition = useElementRelativePosition({
        elementRef: bookButtonsRef,
      })

      const elementHeight = 45
      if (elementRelativePosition.y < -elementHeight) {
        setShowActionBar(true)
      } else {
        setShowActionBar(false)
      }
    },
  })

  const onButtonClick = ({
    selectB2b = false,
    selectProductId = undefined,
  }: {
    selectB2b?: boolean
    selectProductId?: string | undefined
  }) => {
    const selectedProduct = filteredProfileProducts.find(
      (productItem) => productItem.product.id === selectProductId,
    )

    setIsB2b(selectB2b)

    if (selectedProduct) {
      router.push(
        `/profile/${profileSlug}/product/${selectedProduct.product.slug}`,
      )
    } else {
      setShowProductModal(true)
    }
  }

  const headingColor = ['dark', 'light'].find((el) => el !== color)

  const hidePromotedProductsControlSlugs = [
    'donation',
    'tipping',
    'video-greeting-business',
    'video-greeting',
    'video-request-business-internal',
  ]

  const showPromotedProducts =
    filteredProfileProducts?.filter(
      (profileProduct) =>
        !hidePromotedProductsControlSlugs.includes(
          profileProduct?.product?.slug,
        ),
    )?.length !== 0

  return (
    <Wrapper className={styles.profilePage} data-cy="profilePage">
      {profile?.slug && profile?.name && (
        <FeatureFlag.Enabled flagKey="browsability">
          <div className={styles.breadcrumbs}>
            <TagsBreadcrumbs
              tag={mainTag}
              paddingHorizontal="narrow"
              lastBreadcrumb={{
                key: profile.slug,
                href: `/${profile.slug}`,
                title: profile.name,
              }}
            />
          </div>
        </FeatureFlag.Enabled>
      )}

      {profile && profile.slug && <Videos profile={profile} />}
      <div className={styles.gridContainer}>
        <ColumnWrapper className={styles.gridLeftColumn}>
          <div className={styles.gridChild}>
            <Information profile={profile} />
          </div>
          {showPromotedProducts && (
            <div className={cn(styles.gridChild, styles.gridChildProducts)}>
              <Products
                profileProducts={b2cProfileProducts}
                onClick={(productId: string) =>
                  onButtonClick({
                    selectProductId: productId,
                  })
                }
              />
            </div>
          )}
          <div className={cn(styles.gridChild, styles.gridChildDescription)}>
            <Description
              profile={profile}
              showDescription={isDefaultMarket || showDescription}
            />
          </div>

          <div
            className={cn(styles.gridChild, styles.gridChildRelatedCategories)}
          >
            <Categories profileSlug={profileSlug} />
          </div>

          <div className={cn(styles.gridChild, styles.gridChildAboutMemmo)}>
            <AboutMemmo />
          </div>
        </ColumnWrapper>

        <ColumnWrapper className={styles.gridRightColumn}>
          <div
            className={cn(styles.gridChild, styles.gridChildBookCTAs)}
            ref={bookButtonsRef}
          >
            <BookCTAs
              profile={profile}
              products={bookableProducts}
              onButtonClick={onButtonClick}
              profileOffline={profileOffline}
              loading={loading}
              discountBadgeColor={headingColor}
            />
          </div>

          {bookableProducts.haveB2C() && !profileOffline && (
            <div className={cn(styles.gridChild, styles.gridChildTrust)}>
              <TrustSection talentName={profile?.name} />
            </div>
          )}

          {bookableProducts.haveOnlyB2B() && !profileOffline && (
            <div className={styles.b2bOnlyBannerContainer}>
              <B2BColoredBanner profileName={profile?.name} />
            </div>
          )}
        </ColumnWrapper>
      </div>

      {/* Banner to be displayed on all profiles with a B2B product but hidden on
      mobile for profiles with no B2C products as the banner above with take place */}
      {bookableProducts.haveB2B() && !profileOffline && (
        <div
          className={cn(styles.b2bBannerContainer, {
            [styles.hideOnMobile]: bookableProducts.haveOnlyB2B(),
          })}
        >
          <B2BColoredBanner profileName={profile?.name} />
        </div>
      )}

      {showProductModal && (
        <ProductSelect
          profileProducts={isB2b ? bookableProducts.b2b : bookableProducts.b2c}
          profile={profile}
          profileSlug={profileSlug}
          isB2b={isB2b}
          onClose={() => setShowProductModal(false)}
        />
      )}

      {showActionBar &&
        isMobileOnly &&
        !profileOffline &&
        !!bookableProducts.b2c?.length && (
          <NoSSR>
            <ActionBar
              profile={profile}
              loading={loading}
              products={bookableProducts}
              onClick={() => {
                setShowProductModal(true)
                setIsB2b(false)
              }}
            />
          </NoSSR>
        )}

      {profile?.id && (
        <RecommendedProfileCollection
          market={market.id as string}
          profileId={profile.id}
        />
      )}
      <ProfileCollections />
      <RecentlyViewedProfiles profileSlug={profileSlug} />

      {profile?.seoContent && isDefaultMarket && (
        <section
          className={styles.seoContent}
          data-cy="profilePageDescriptionSeo"
        >
          <MarkdownContent source={profile?.seoContent} />
        </section>
      )}
    </Wrapper>
  )
}

export default ProfilePage
